import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, Edit, Refresh } from '@mui/icons-material';
import Navbar from './Navbar';
import { fetchPaperSizes, savePaperSize } from '../../utils/paperSizeAPI';

const EMPTY_FORM = {
  sizeID: null,
  sizeName: '',
  paperMm: 80,
  charsPerLine: 48,
  isActive: true
};

const statusColor = (isActive) => (Number(isActive) === 1 ? 'success' : 'default');

export default function PaperSizeManagement() {
  const [paperSizes, setPaperSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadSizes = async () => {
    try {
      setLoading(true);
      const sizes = await fetchPaperSizes({ includeInactive: true });
      setPaperSizes(Array.isArray(sizes) ? sizes : []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load paper sizes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSizes();
  }, []);

  const clearMessagesSoon = () => {
    window.setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const openForCreate = () => {
    setFormData(EMPTY_FORM);
    setOpenDialog(true);
  };

  const openForEdit = (item) => {
    setFormData({
      sizeID: item.sizeID,
      sizeName: item.sizeName || '',
      paperMm: Number(item.paperMm || 80),
      charsPerLine: Number(item.charsPerLine || 48),
      isActive: Number(item.isActive) === 1
    });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setFormData(EMPTY_FORM);
  };

  const handleChange = (key) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggle = (_event, checked) => {
    setFormData((prev) => ({ ...prev, isActive: checked }));
  };

  const handleSave = async () => {
    const payload = {
      sizeID: formData.sizeID,
      sizeName: String(formData.sizeName || '').trim(),
      paperMm: Number(formData.paperMm || 0),
      charsPerLine: Number(formData.charsPerLine || 0),
      isActive: formData.isActive
    };

    if (!payload.sizeName) {
      setError('Size name is required.');
      return;
    }

    if (!payload.paperMm || payload.paperMm <= 0) {
      setError('Valid paper width (mm) is required.');
      return;
    }

    if (!payload.charsPerLine || payload.charsPerLine <= 0) {
      setError('Valid chars per line is required.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await savePaperSize(payload);
      setSuccess(formData.sizeID ? 'Paper size updated successfully.' : 'Paper size created successfully.');
      closeDialog();
      loadSizes();
      clearMessagesSoon();
    } catch (err) {
      setError(err?.message || 'Failed to save paper size.');
    }
  };

  const filteredRows = paperSizes.filter((item) => {
    if (statusFilter === 'active') return Number(item.isActive) === 1;
    if (statusFilter === 'inactive') return Number(item.isActive) === 0;
    return true;
  });

  return (
    <>
      <Navbar />
      <Box sx={{ p: 2 }}>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              Paper Size Management
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadSizes}>
                Refresh
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openForCreate}>
                Add Size
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            {loading && <Typography variant="body2">Loading...</Typography>}
          </Stack>

          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Size Name</TableCell>
                  <TableCell>Paper (mm)</TableCell>
                  <TableCell>Chars / Line</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No paper size found.</TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <TableRow key={row.sizeID}>
                      <TableCell>{row.sizeID}</TableCell>
                      <TableCell>{row.sizeName}</TableCell>
                      <TableCell>{row.paperMm}</TableCell>
                      <TableCell>{row.charsPerLine}</TableCell>
                      <TableCell>
                        <Chip size="small" label={Number(row.isActive) === 1 ? 'Active' : 'Inactive'} color={statusColor(row.isActive)} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<Edit />} onClick={() => openForEdit(row)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{formData.sizeID ? 'Edit Paper Size' : 'Add Paper Size'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Size Name"
              value={formData.sizeName}
              onChange={handleChange('sizeName')}
              fullWidth
            />
            <TextField
              label="Paper Width (mm)"
              type="number"
              inputProps={{ min: 1 }}
              value={formData.paperMm}
              onChange={handleChange('paperMm')}
              fullWidth
            />
            <TextField
              label="Characters Per Line"
              type="number"
              inputProps={{ min: 1 }}
              value={formData.charsPerLine}
              onChange={handleChange('charsPerLine')}
              fullWidth
            />
            <FormControlLabel
              control={<Switch checked={Boolean(formData.isActive)} onChange={handleToggle} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
